import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { track } from '../utils/track'
import type { ReleaseFilters } from '../types/release'
import {
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getFolderReleases,
  getFolderReleaseIds,
  addReleaseToFolder,
  removeReleaseFromFolder,
  getReleaseFolders,
} from '../api/folders'

export const foldersKeys = {
  all: ['folders'] as const,
  detail: (id: number) => ['folders', id] as const,
  releases: (id: number, filters?: ReleaseFilters) => ['folders', id, 'releases', filters] as const,
  releaseIds: (id: number) => ['folders', id, 'release-ids'] as const,
  releaseFolder: (releaseId: number) => ['folders', 'release', releaseId] as const,
}

// Get all folders
export function useFolders() {
  return useQuery({
    queryKey: foldersKeys.all,
    queryFn: getFolders,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Get folder details with releases
export function useFolderDetail(id: number, filters: ReleaseFilters = {}) {
  const [debouncedQuery, setDebouncedQuery] = useState(filters.q)
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(filters.q), 450)
    return () => window.clearTimeout(timeout)
  }, [filters.q])
  const queryFilters = useMemo(
    () => ({ ...filters, q: debouncedQuery }),
    [filters, debouncedQuery]
  )

  return useQuery({
    queryKey: foldersKeys.releases(id, queryFilters),
    queryFn: () => getFolderReleases(id, queryFilters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

// Get all release IDs in a folder (for recommendations)
export function useFolderReleaseIds(id: number, enabled = false) {
  return useQuery({
    queryKey: foldersKeys.releaseIds(id),
    queryFn: () => getFolderReleaseIds(id),
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 2,
  })
}

// Get folders for a specific release
export function useReleaseFolders(releaseId: number) {
  return useQuery({
    queryKey: foldersKeys.releaseFolder(releaseId),
    queryFn: () => getReleaseFolders(releaseId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Create folder
export function useCreateFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color?: string }) => createFolder(name, color),
    onSuccess: () => {
      track('folder_create')
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })
    },
  })
}

// Update folder
export function useUpdateFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: { name?: string; color?: string } }) =>
      updateFolder(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })
      queryClient.invalidateQueries({ queryKey: foldersKeys.detail(variables.id) })
    },
  })
}

// Delete folder
export function useDeleteFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })
    },
  })
}

// Add release to folder
export function useAddReleaseToFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, releaseId }: { folderId: number; releaseId: number }) =>
      addReleaseToFolder(folderId, releaseId),
    onSuccess: (_, variables) => {
      // Invalidate folder list to update counts
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })
      // Invalidate all queries for this folder (all pages)
      queryClient.invalidateQueries({ queryKey: ['folders', variables.folderId] })
      // Invalidate release folders
      queryClient.invalidateQueries({ queryKey: foldersKeys.releaseFolder(variables.releaseId) })
    },
  })
}

// Remove release from folder
export function useRemoveReleaseFromFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, releaseId }: { folderId: number; releaseId: number }) =>
      removeReleaseFromFolder(folderId, releaseId),
    onSuccess: (_, variables) => {
      // Invalidate folder list to update counts
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })
      // Invalidate all queries for this folder (all pages)
      queryClient.invalidateQueries({ queryKey: ['folders', variables.folderId] })
      // Invalidate release folders
      queryClient.invalidateQueries({ queryKey: foldersKeys.releaseFolder(variables.releaseId) })
    },
  })
}
